/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback, useContext, useState } from 'react';

import type { InlineNotificationProps } from '@coursera/cds-core';
import { InlineNotification } from '@coursera/cds-core';

export type NotificationMessage = {
  content: string;
  severity: InlineNotificationProps['severity'];
  isTransient?: boolean;
};

export const TRANSIENT_DURATION_MS = 3000;
// for durations longer than this, consider using a non-transient notification instead
export const TRANSIENT_DURATION_LONG_MS = 6000;

type NotificationContextType = {
  addNotification: (message: NotificationMessage) => void;
  clearNotification: () => void;
};
const NotificationContext = React.createContext<NotificationContextType>({
  addNotification: () => undefined,
  clearNotification: () => undefined,
});

const styles = {
  notifications: css`
    position: absolute;
    max-width: 400px;
    z-index: 10000;
    margin: 0 auto;
    left: var(--cds-spacing-200);
    right: var(--cds-spacing-200);
    top: 64px;
    opacity: 1;

    > div {
      box-shadow: 0 6px 24px 0 rgb(0 0 0 / 10%);
      animation: fadeIn 0.3s ease-in-out;
      opacity: 1;
    }

    @keyframes fadeIn {
      from {
        opacity: 0;
      }

      to {
        opacity: 1;
      }
    }
  `,
};

/** Hook to add an inline notification to any setting page */
export const useSettingsNotification = () => useContext(NotificationContext);

export const SettingsNotificationProvider = ({ children }: React.PropsWithChildren) => {
  const [notification, setNotification] = useState<NotificationMessage | null>(null);

  const clearNotification = useCallback<() => void>(() => {
    setNotification(null);
  }, []);

  const addNotification = useCallback<(notif: NotificationMessage) => void>(
    (notif: NotificationMessage) => {
      setNotification(notif);

      if (notif.isTransient) {
        // set transient duration dynamically based on content length being shown for better readability
        // on average, 40 characters roughly equates to 7 words which is read on average in under 3 seconds
        const duration = notif.content.length > 40 ? TRANSIENT_DURATION_LONG_MS : TRANSIENT_DURATION_MS;
        setTimeout(() => {
          clearNotification();
        }, duration);
      }
    },
    [clearNotification]
  );

  return (
    <NotificationContext.Provider value={{ addNotification, clearNotification }}>
      <div css={styles.notifications} aria-live="assertive">
        {notification ? (
          <InlineNotification
            severity={notification.severity}
            onDismiss={notification.isTransient ? undefined : clearNotification}
          >
            {notification.content}
          </InlineNotification>
        ) : null}
      </div>
      {children}
    </NotificationContext.Provider>
  );
};
